import { useState } from "react";
import PageHeader from "../components/PageHeader";
import { jobs } from "../lib/mockData";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Briefcase, MapPin, DollarSign, Clock, Search } from "lucide-react";
import { toast } from "sonner";

const FILTERS = ["All", "Full-time", "Part-time", "Contract", "Gig"];

export default function Jobs() {
  const [filter, setFilter] = useState("All");
  const [q, setQ] = useState("");
  const filtered = jobs.filter(j =>
    (filter === "All" || j.type === filter) &&
    (q === "" || j.title.toLowerCase().includes(q.toLowerCase()) || j.company.toLowerCase().includes(q.toLowerCase()))
  );

  return (
    <>
      <PageHeader
        eyebrow="Jobs & Gigs"
        title="Fair work, near and far."
        description="Meaningful jobs from mission-driven employers and community organizations."
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 pb-16">
        <div className="flex flex-col md:flex-row gap-3 mb-8">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid="jobs-search"
              placeholder="Search by title or company..."
              className="pl-11 h-12 rounded-full border-border"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            {FILTERS.map(f => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                data-testid={`jobs-filter-${f.toLowerCase()}`}
                className={`px-4 h-12 rounded-full text-sm font-medium transition-colors ${
                  filter === f ? "bg-primary text-primary-foreground" : "bg-white border border-border hover:bg-muted"
                }`}
              >
                {f}
              </button>
            ))}
          </div>
        </div>

        <div className="grid gap-4">
          {filtered.map((j, i) => (
            <Card
              key={j.id}
              data-testid={`job-${j.id}`}
              className={`fade-up stagger-${(i % 6) + 1} rounded-3xl border-border/60 shadow-none hover:shadow-md hover:border-primary/40 transition-all p-6 sm:p-7`}
            >
              <div className="flex flex-col sm:flex-row sm:items-center gap-5">
                <div className="h-14 w-14 rounded-2xl bg-primary/10 text-primary flex items-center justify-center shrink-0">
                  <Briefcase size={22} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    {j.tags.map(t => (
                      <Badge key={t} className="rounded-full bg-secondary/15 text-secondary border-0 text-[10px] px-2">{t}</Badge>
                    ))}
                  </div>
                  <h3 className="font-display text-xl font-semibold">{j.title}</h3>
                  <div className="text-sm text-muted-foreground mt-0.5">{j.company}</div>
                  <div className="mt-3 flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1"><MapPin size={12} /> {j.location}</span>
                    <span className="flex items-center gap-1"><DollarSign size={12} /> {j.pay}</span>
                    <span className="flex items-center gap-1"><Clock size={12} /> {j.posted}</span>
                    <Badge variant="outline" className="rounded-full text-[10px] border-border">{j.type}</Badge>
                  </div>
                </div>
                <Button
                  onClick={() => toast.success(`Application sent to ${j.company}`)}
                  data-testid={`job-apply-${j.id}`}
                  className="rounded-full bg-primary hover:bg-primary/90 shrink-0"
                >
                  Apply
                </Button>
              </div>
            </Card>
          ))}
          {filtered.length === 0 && (
            <div className="text-center py-16 text-muted-foreground" data-testid="jobs-empty">
              No jobs match your search yet — try widening your filter.
            </div>
          )}
        </div>
      </div>
    </>
  );
}
